import { useEffect, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import './HomeVisit.scss';
import DateTimePicker from './NurseAvailability';
import { getRequest } from '@/services/api';
import HomeVisitMap from './NurseMap';
import { Chip } from '@mui/material';
import { formatNameWithInitials } from '@/helpers/formatName';

const defaultCenter = {
	lat: 51.505,
	lng: -0.09,
};

function calculateAge(dateOfBirth) {
	if (!dateOfBirth) return 0;
	const dob = new Date(dateOfBirth);
	const diff = Date.now() - dob.getTime();
	const ageDate = new Date(diff);
	return Math.abs(ageDate.getUTCFullYear() - 1970);
}

function calculateDistance(lat1, lng1, lat2, lng2) {
	const R = 6371; // radius of Earth in km
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLng = ((lng2 - lng1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	const distanceInKm = R * c;
	const distanceInMiles = distanceInKm / 1.60934;
	return distanceInMiles;
}

function transformAvailability(availabilityArray) {
	const result = {};
	if (!availabilityArray || !Array.isArray(availabilityArray)) return result;
	availabilityArray.forEach((block) => {
		if (!block.date || !Array.isArray(block.intervals)) return;
		result[block.date] = {
			businessOpen: true,
			availableTimeSlots: block.intervals,
		};
	});
	return result;
}

const NurseSelection = ({ formData, setFormData, noAdditionalCost }) => {
	const [filteredNurses, setFilteredNurses] = useState([]);
	const [userLocation, setUserLocation] = useState(null);
	const [loading, setLoading] = useState(false);

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
	});

	useEffect(() => {
		const { addressLine1, city, postcode } = formData;
		const userFullAddress = [addressLine1, city, postcode, 'United Kingdom']
			.filter((p) => p && p.trim() !== '')
			.join(', ');
		if (userFullAddress) {
			geocodeAddress(userFullAddress).then(setUserLocation);
		}
	}, []);

	// Fetch nurses only when userLocation and essential patient data (address & DOB) change.
	useEffect(() => {
		const { addressLine1, city, postcode, dateOfBirth } = formData;
		if (!userLocation || !addressLine1 || !city || !postcode || !dateOfBirth) return;

		const fetchNurses = async () => {
			try {
				setLoading(true);
				const patientAge = calculateAge(dateOfBirth);

				const apiNurses = await loadNursesFromAPI();

				const processedNurses = [];
				for (const nurse of apiNurses) {
					const youngestAllowed =
						nurse.youngest_age_phlebotomy != null ? parseFloat(nurse.youngest_age_phlebotomy) : 0;
					if (patientAge < youngestAllowed) {
						// Skip this nurse if patient is too young
						continue;
					}

					const nurseFullAddress = [nurse.address_line1, nurse.city, nurse.postcode, nurse.country]
						.filter((p) => p && p.trim() !== '')
						.join(', ');

					let nurseLatLng = null;
					if (nurseFullAddress) {
						nurseLatLng = await geocodeAddress(nurseFullAddress);
					}
					if (!nurseLatLng) {
						continue;
					}

					let distanceInMiles = 0;
					if (userLocation && nurseLatLng) {
						distanceInMiles = calculateDistance(
							userLocation.lat,
							userLocation.lng,
							nurseLatLng.lat,
							nurseLatLng.lng
						);
					}

					if (distanceInMiles > 20) {
						continue;
					}

					const dateTimePickerAvail = transformAvailability(nurse.availability);
					processedNurses.push({
						id: nurse.user_id || nurse.id,
						name: formatNameWithInitials(nurse.full_name),
						latitude: nurseLatLng?.lat || defaultCenter.lat,
						longitude: nurseLatLng?.lng || defaultCenter.lng,
						distanceInMiles: distanceInMiles.toFixed(2),
						youngestPatient: youngestAllowed,
						availability: dateTimePickerAvail,
						price: nurse.first_person_rate,
					});
				}
				// Sort nurses by distance and limit to top 3
				processedNurses.sort(
					(a, b) => parseFloat(a.distanceInMiles) - parseFloat(b.distanceInMiles)
				);
				const topThree = processedNurses.slice(0, 3);
				setFilteredNurses(topThree);
			} catch (error) {
				console.error('Error fetching nurses:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchNurses();
	}, [userLocation]);

	const geocodeAddress = async (address) => {
		try {
			const response = await fetch(
				`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
					address
				)}&key=${process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY}`
			);
			const data = await response.json();
			return data.results?.[0]?.geometry?.location || null; // {lat, lng}
		} catch (error) {
			console.error('Geocoding error:', error);
			return null;
		}
	};

	async function loadNursesFromAPI() {
		const queryParams = {
			allowed_to_home_visit: 'true',
		};
		const data = await getRequest('/nurse/filter', queryParams);
		if (data && data.status === 'success') {
			return data.results;
		}
		console.error('Unexpected nurse API response:', data);
		return [];
	}

	const handleNurseDateTimeChange = ({ nurseId, date, time }) => {
		const chosenNurse = filteredNurses.find((nurse) => nurse.id === nurseId) || {};
		setFormData((prev) => ({
			...prev,
			selectedNurseId: nurseId,
			selectedNurseName: chosenNurse.name,
			selectedNursePrice: noAdditionalCost ? null : chosenNurse.price,
			selectedDate: date,
			selectedTime: time,
		}));
	};

	return (
		<div className="home-visit-wrapper">
			{loading && <div className="spinner d-flex justify-content-center">Loading...</div>}
			<div className="map-availability-view">
				<div className="results-container">
					<div className="map-section">
						{isLoaded ? (
							<HomeVisitMap userLocation={userLocation} nurses={filteredNurses} />
						) : (
							<p>Loading Map...</p>
						)}
					</div>
					<div className="nurses-section">
						<h3>Nearest Clinician</h3>
						{loading ? (
							<div
								className="d-flex justify-content-center align-items-center"
								style={{ minHeight: '300px' }}
							>
								<div className="spinner-border" role="status">
									<span className="sr-only">Loading...</span>
								</div>
							</div>
						) : filteredNurses.length === 0 ? (
							<>
								<br />
								<p>No available clinician found near your location.</p>
								<p>Please reach out to us to make an appointment.</p>
								<p>
									<a href="https://www.sirkkahealth.com/contact">
										{' '}
										<b>Contact us here</b>
									</a>
								</p>
							</>
						) : (
							filteredNurses.map((nurse) => (
								<div key={nurse.id} className="nurse-card">
									<div className="nurse-header-row">
										<div className="nurse-name-col">
											<h3>
												{nurse.name}
												{!noAdditionalCost && nurse.price && (
													<Chip
														className="nurse-rate"
														label={`+ £ ${(parseFloat(nurse.price) + 10).toFixed(2)}`}
														size="small"
													/>
												)}
											</h3>
										</div>
										<div className="distance-col">
											<span>{nurse.distanceInMiles} miles</span>
										</div>
									</div>
									<DateTimePicker
										key={nurse.id}
										nurseId={nurse.id}
										availabilityData={nurse.availability}
										onDateTimeChange={handleNurseDateTimeChange}
										selectedNurseId={formData.selectedNurseId}
										selectedDate={formData.selectedDate}
										selectedTime={formData.selectedTime}
									/>
								</div>
							))
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default NurseSelection;
